import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";

/**
 * IntegrationsScripts
 * -------------------
 * Fetches the public pixel config from `/api/integrations/public-config`
 * and injects Meta Pixel, GA4 (gtag), Google Ads, TikTok Pixel and Snap
 * Pixel into the page. Fires PageView on every route change and exposes
 * `window.__shamsFirePurchase(orderId, total, items)` for the checkout
 * confirmation page to call after a successful order.
 *
 * Nothing loads if no integrations are enabled — zero perf cost.
 */
export default function IntegrationsScripts() {
  const cfgRef = useRef(null);
  const location = useLocation();

  // Load & inject scripts once
  useEffect(() => {
    let cancelled = false;
    api.get("/integrations/public-config")
      .then(({ data }) => {
        if (cancelled) return;
        cfgRef.current = data || {};
        injectAll(data || {});
        firePageView(data || {}, location.pathname);
        // Expose global purchase-fire helper for OrderSuccess.jsx
        window.__shamsFirePurchase = (orderId, total, items) =>
          firePurchase(cfgRef.current || {}, { orderId, total, items });
      })
      .catch(() => { /* pixels are best-effort */ });
    return () => { cancelled = true; };
  }, []);

  // Fire pageview on every route change
  useEffect(() => {
    if (!cfgRef.current) return;
    firePageView(cfgRef.current, location.pathname);
  }, [location.pathname]);

  return null;
}

// --------- Injectors ---------
function injectScript(id, srcOrCode, inline = false) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  if (inline) s.textContent = srcOrCode;
  else s.src = srcOrCode;
  document.head.appendChild(s);
}

function injectAll(cfg) {
  // Meta Pixel
  const meta = cfg.meta_pixel;
  if (meta?.pixel_id && !window.fbq) {
    injectScript(
      "meta-pixel-loader",
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${meta.pixel_id}');`,
      true
    );
  }

  // GA4 (gtag)
  const ga4 = cfg.ga4;
  const googleAds = cfg.google_ads;
  const gtagIds = [];
  if (ga4?.measurement_id) gtagIds.push(ga4.measurement_id);
  if (googleAds?.conversion_id) gtagIds.push(googleAds.conversion_id);
  if (gtagIds.length && !window.gtag) {
    injectScript(
      "gtag-loader",
      `https://www.googletagmanager.com/gtag/js?id=${gtagIds[0]}`
    );
    injectScript(
      "gtag-init",
      `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js', new Date());${gtagIds.map(id => `gtag('config','${id}');`).join("")}`,
      true
    );
  }

  // TikTok Pixel
  const tt = cfg.tiktok;
  if (tt?.pixel_id && !window.ttq) {
    injectScript(
      "tiktok-pixel-loader",
      `!function (w, d, t) {w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};ttq.load('${tt.pixel_id}');ttq.page();}(window, document, 'ttq');`,
      true
    );
  }

  // Snap Pixel
  const snap = cfg.snap;
  if (snap?.pixel_id && !window.snaptr) {
    injectScript(
      "snap-pixel-loader",
      `(function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function(){a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};a.queue=[];var s='script';r=t.createElement(s);r.async=!0;r.src=n;var u=t.getElementsByTagName(s)[0];u.parentNode.insertBefore(r,u);})(window,document,'https://sc-static.net/scevent.min.js');snaptr('init','${snap.pixel_id}');`,
      true
    );
  }
}

function firePageView(cfg, path) {
  try {
    if (cfg.meta_pixel?.pixel_id && window.fbq) window.fbq("track", "PageView");
    if (cfg.ga4?.measurement_id && window.gtag) {
      window.gtag("event", "page_view", { page_path: path });
    }
    if (cfg.tiktok?.pixel_id && window.ttq) window.ttq.page();
    if (cfg.snap?.pixel_id && window.snaptr) window.snaptr("track", "PAGE_VIEW");
  } catch (e) {
    console.debug("pixel pageview failed:", e);
  }
}

function firePurchase(cfg, { orderId, total, items }) {
  const value = Number(total) || 0;
  const contents = (items || []).map((it) => ({
    id: it.product_id || it.sku || "",
    quantity: Number(it.quantity) || 1,
    item_price: Number(it.price) || 0,
    name: it.name || "",
  }));
  try {
    // Meta
    if (cfg.meta_pixel?.pixel_id && window.fbq) {
      window.fbq("track", "Purchase", {
        value, currency: "JOD",
        content_ids: contents.map((c) => c.id).filter(Boolean),
        content_type: "product",
        contents: contents.map((c) => ({ id: c.id, quantity: c.quantity })),
        num_items: contents.reduce((s, c) => s + c.quantity, 0),
      }, { eventID: orderId });
    }
    // GA4
    if (cfg.ga4?.measurement_id && window.gtag) {
      window.gtag("event", "purchase", {
        transaction_id: orderId, value, currency: "JOD",
        items: contents.map((c) => ({
          item_id: c.id, item_name: c.name,
          quantity: c.quantity, price: c.item_price,
        })),
      });
    }
    // Google Ads
    if (cfg.google_ads?.conversion_id && cfg.google_ads?.conversion_label && window.gtag) {
      window.gtag("event", "conversion", {
        send_to: `${cfg.google_ads.conversion_id}/${cfg.google_ads.conversion_label}`,
        value, currency: "JOD",
        transaction_id: orderId,
      });
    }
    // TikTok
    if (cfg.tiktok?.pixel_id && window.ttq) {
      window.ttq.track("PlaceAnOrder", {
        contents: contents.map((c) => ({
          content_id: c.id, content_name: c.name,
          quantity: c.quantity, price: c.item_price,
        })),
        value, currency: "JOD", order_id: orderId,
      });
    }
    // Snap
    if (cfg.snap?.pixel_id && window.snaptr) {
      window.snaptr("track", "PURCHASE", {
        price: value, currency: "JOD", transaction_id: orderId,
      });
    }
  } catch (e) {
    console.debug("pixel purchase failed:", e);
  }
}
